import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ILanguageFormData } from "../../types";
import { languageSettingsSchema } from "../../validations";
import { INITIAL_LANGUAGE_SETTINGS } from "../configs";
import { useGetKioskLanguageQuery } from "../services/kioskApiSlice";
import { getLanguageI18nKey } from "../utils";
import useKioskSettings from "./useKioskSettings";
import { useTabGuard } from "./useTabGuard";

interface UseLanguageSettingsProps {
    characterId: string;
}

export const useLanguageSettings = ({ characterId }: UseLanguageSettingsProps) => {
    const { t } = useTranslation();
    const { kioskSetting, isUpdatingKioskSetting, updateLanguage } = useKioskSettings({ characterId });

    const { data: availableLanguageCodes, isLoading: isLoadingLanguageOptions } = useGetKioskLanguageQuery();

    const methods = useForm<ILanguageFormData>({
        mode: "all",
        resolver: yupResolver(languageSettingsSchema()),
        defaultValues: INITIAL_LANGUAGE_SETTINGS,
    });

    // 把 Language Codes 轉換為 i18n 的 label
    const languageOptions = useMemo(() => {
        if (!availableLanguageCodes || availableLanguageCodes.length === 0) return [];
        return availableLanguageCodes.map((code) => ({
            value: code,
            label: t(getLanguageI18nKey(code)),
        }));
    }, [availableLanguageCodes, t]);

    // 用戶已選擇的語言（用於預設語言下拉選單）
    const selectedLanguageCodes = useWatch({ control: methods.control, name: "languageOptions" });
    const selectedLanguageOptions = useMemo(
        () => languageOptions.filter((opt) => (selectedLanguageCodes || []).includes(opt.value as string)),
        [languageOptions, selectedLanguageCodes]
    );

    const isDirty = methods.formState.isDirty;
    const isValid = methods.formState.isValid;

    const handleSubmit = useCallback(
        async (data: ILanguageFormData) => {
            await updateLanguage({
                languageOptions: data.languageOptions,
                defaultLanguage: data.defaultLanguage,
                languageDisplayMode: data.languageDisplayMode,
            });
        },
        [updateLanguage]
    );

    const saveHandler = useCallback(async () => {
        await methods.handleSubmit(handleSubmit)();
    }, [methods, handleSubmit]);

    useTabGuard("language", isDirty, saveHandler);

    const handleCancel = () => {
        methods.reset();
    };

    const handleResetToDefaults = () => {
        methods.setValue("languageOptions", INITIAL_LANGUAGE_SETTINGS.languageOptions, {
            shouldDirty: true,
        });
        methods.setValue("defaultLanguage", INITIAL_LANGUAGE_SETTINGS.defaultLanguage, {
            shouldDirty: true,
        });
        methods.setValue("languageDisplayMode", INITIAL_LANGUAGE_SETTINGS.languageDisplayMode, {
            shouldDirty: true,
        });
    };

    // 當 kioskSetting 載入後，更新表單初始值
    useEffect(() => {
        if (kioskSetting?.data?.native?.general) {
            const { languageOptions, defaultLanguage, languageDisplayMode } = kioskSetting.data.native.general;
            methods.reset({
                languageOptions: languageOptions || INITIAL_LANGUAGE_SETTINGS.languageOptions,
                defaultLanguage: defaultLanguage || INITIAL_LANGUAGE_SETTINGS.defaultLanguage,
                languageDisplayMode: languageDisplayMode || INITIAL_LANGUAGE_SETTINGS.languageDisplayMode,
            });
        }
    }, [kioskSetting, methods]);

    return {
        methods,
        handleSubmit,
        handleCancel,
        handleResetToDefaults,
        languageOptions,
        selectedLanguageOptions,
        isLoadingLanguageOptions,
        isUpdatingKioskSetting,
        isDirty,
        isValid,
    };
};
